import React from 'react';
import { cn } from '../../lib/utils';
import { TONOS } from './tonos';

/**
 * Un feature principal, en una fila: la captura de un lado y la explicación del
 * otro.
 *
 * Es el primer nivel de la sección de producto. El segundo es la lista
 * tipográfica de las funcionalidades menores. La sección estuvo siendo sólo un
 * mosaico de celdas, y ahí las once funcionalidades pesaban casi lo mismo: una
 * lista disfrazada de grilla, sin nada explicado de verdad y sin nada donde
 * apoyar la vista. Dos o tres filas arriba y el resto abajo es esa jerarquía.
 *
 * NO SABE NADA DEL FEATURE QUE MUESTRA. El visual entra como children y el
 * texto por props, así que cada fila monta el suyo. Escribir las tres a mano es
 * lo que se evita: tres bloques hechos por separado se despegan en el
 * interlineado, el tamaño del título y el aire entre columnas, y la sección
 * deja de leerse como una.
 *
 *
 * TRES DECISIONES QUE VAN CONTRA LA VERSIÓN ANTERIOR DE ESTE MISMO ARCHIVO
 *
 * 1. EL TITULAR ES EL PROBLEMA, NO EL NOMBRE DEL FEATURE. Antes decía "Página
 *    de reservas" y ahora dice "Quiere reservar a las once de la noche".
 *    "Bot de WhatsApp con IA" es una categoría, no un anzuelo: nadie se
 *    levantó queriendo un bot. El nombre del feature pasa a `etiqueta`, en
 *    chico y arriba, para quien viene buscando la ficha técnica.
 *
 * 2. NO HAY ÍCONO. Tenía un círculo de color con un ícono de lucide al lado
 *    del título, y celditas con ícono son hoy la señal más reconocible de
 *    landing generada con IA. Lo que ilustra la fila es la captura del
 *    producto, que es la prueba; un pictograma al lado sólo compite con ella.
 *
 * 3. LAS COLUMNAS SON DESIGUALES (1 a 1,35 en escritorio). El 50/50 espejado
 *    es lo que hace que una página se sienta plantilla, y además el reparto
 *    justo no refleja lo que cada columna tiene que hacer: el texto son cuatro
 *    líneas y la captura es la evidencia. En teléfono se apila y la proporción
 *    no corre.
 *
 * @param etiqueta   El nombre del feature, chico y arriba del titular.
 * @param tono       Clave de `tonos.js`. Pinta la etiqueta y los tildes.
 * @param titulo     EL PROBLEMA, en grande. Es el titular de la fila.
 * @param bajada     Una o dos líneas de cómo se resuelve.
 * @param puntos     Lista corta de lo que hace. Vacía, no se dibuja.
 * @param nota       Letra chica de abajo. Es donde va la aclaración de plan
 *                   cuando el feature no está en todos: una fila grande que
 *                   promete algo que el plan de entrada no tiene es cómo
 *                   alguien compra Starter esperando el bot.
 * @param visualAngosto  El visual es más alto que ancho —un teléfono— y por
 *                   lo tanto la columna ANCHA le queda grande: centrado ahí
 *                   deja dos huecos muertos a los lados. Con esto la
 *                   proporción se da vuelta y la columna grande pasa a ser la
 *                   del texto, que es lo que tiene material para llenarla.
 * @param invertida  Da vuelta las columnas en escritorio. En teléfono se
 *                   ignora: apilado, el visual va SIEMPRE primero —es el
 *                   gancho, y alternar el orden en una sola columna no se lee
 *                   como alternancia, se lee como desprolijidad.
 * @param children   El visual.
 */
export default function FilaDeFuncionalidad({
  etiqueta,
  tono = 'naranja',
  titulo,
  bajada,
  puntos = [],
  nota,
  visualAngosto = false,
  invertida = false,
  children,
}) {
  const { chip, icono } = TONOS[tono] ?? TONOS.naranja;

  return (
    <article
      className={cn(
        'grid grid-cols-1 items-center gap-10 lg:gap-16',
        /* CUÁL DE LAS DOS COLUMNAS ES LA ANCHA depende de dos cosas: de qué
           lado está el visual (`invertida`) y de si el visual sabe
           aprovecharla (`visualAngosto`). Una captura de escritorio quiere la
           ancha; un teléfono, la angosta —centrado en la grande deja dos
           huecos muertos a los lados—. El visual es el primer hijo, así que
           sin invertir cae en la primera columna y con `invertida` se va a la
           segunda por `lg:order-2`: de ahí que el XOR resuelva los cuatro
           casos con una sola condición.

           Desigual y no 50/50: el espejo es lo que hace que una página se
           sienta plantilla, y el reparto justo tampoco refleja lo que cada
           columna tiene que hacer.

           `minmax(0,…)` Y NO `1.35fr` A SECAS. Un `fr` es en realidad
           `minmax(auto, 1.35fr)`, y ese mínimo `auto` es el min-content de lo
           que hay adentro: como adentro hay un <img> de 2880px de ancho
           intrínseco, la columna se estiraba muy por encima de su proporción y
           la captura terminaba montada arriba del texto de al lado. */
        (invertida !== visualAngosto)
          ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]'
          : 'lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]'
      )}
    >
      {/* `min-w-0` en las dos columnas: sin eso una celda de grilla no baja de
          lo que mida su contenido más ancho, y una captura ancha empujaba la
          columna del texto fuera de la fila. */}
      <div className={cn('min-w-0', invertida && 'lg:order-2')}>{children}</div>

      <div className={cn('min-w-0', invertida && 'lg:order-1')}>
        {etiqueta && (
          <span
            className={cn(
              'inline-block rounded-full px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.12em]',
              chip,
              icono
            )}
          >
            {etiqueta}
          </span>
        )}

        {/* Bricolage y no la tipografía del cuerpo: es la misma familia del
            titular del hero, y eso es lo que hace que la sección se lea como
            continuación de la página y no como otro sitio pegado abajo. */}
        <h3 className="mt-5 text-balance font-titular text-[clamp(1.9rem,3.1vw,2.9rem)] font-bold leading-[1.05] tracking-[-0.03em] text-neutral-950">
          {titulo}
        </h3>

        <p className="mt-5 text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg">
          {bajada}
        </p>

        {puntos.length > 0 && (
          <ul className="mt-7 flex flex-col gap-3">
            {puntos.map((punto) => (
              <li key={punto} className="flex items-start gap-3">
                {/* Un punto y no un tilde dentro de un círculo: el tilde
                    prometía "incluido" en una lista que acá describe cómo
                    funciona la cosa, no qué trae el plan. */}
                <span
                  className={cn('mt-[0.6rem] h-1.5 w-1.5 shrink-0 rounded-full', chip, 'bg-current', icono)}
                  aria-hidden="true"
                />
                <span className="text-base leading-relaxed text-neutral-700">{punto}</span>
              </li>
            ))}
          </ul>
        )}

        {nota && <p className="mt-7 text-sm leading-relaxed text-neutral-500">{nota}</p>}
      </div>
    </article>
  );
}
